import 'ag-grid-enterprise'
import { useSelector } from 'react-redux'
import { selectIdeaListInput } from '@/redux/slices/idea-list-input.slice.ts'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ColDef, GetRowIdParams, RowClassRules } from 'ag-grid-community'
import { FilterChangedEvent, ValueFormatterParams } from 'ag-grid-enterprise'
import type { ColumnEvent, SortChangedEvent } from 'ag-grid-community'
import {
  checkStateColoredNumberCellStyle,
  currencyValueFormatter,
  percentageValueFormatter,
} from '@/utils/formatters.ts'
import { sortMappingIdeaList } from './idea-list-table.def.ts'
import {
  CloseCircleFilled,
  EditOutlined,
  ExclamationCircleFilled,
  InfoCircleFilled,
} from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '@/redux/hooks.ts'
import {
  selectTheme,
  selectThemeStyle,
} from '@/features/setting/setting.slice.ts'
import { Theme } from '@/features/setting/setting.def.ts'
import { IDEA_LIST_COLUMN_STATE_KEY } from '@/utils/constants.ts'
import { isHistoricalEndDate } from '@/utils/time.ts'
import { CustomCellRendererProps } from 'ag-grid-react'
import { IdeyaButton } from '@/common/antd/ideya-button/ideya-button.tsx'
import {
  blockedFirmIdsCreateEditIdea,
  canAccessSideMenuDashboardPermissions,
  hasPermission,
} from '@/utils/permissions.ts'
import { useFixedBreakpoint } from '@/common/hooks/use-is-mobile.ts'
import { IdeaDirectionTag } from './idea-direction-tag.tsx'
import { useTranslation } from 'react-i18next'
import { selectUser } from '@/features/user/user.slice.ts'
import { IsTrialCellRenderer } from '@/pages/components/is-trial-cell-renderer.tsx'
import Tooltip from 'antd/es/tooltip/index'
import { IdeaAlertType } from '@/enums/idea-list.enum.ts'
import {
  useLazyListIdeasQuery,
  useLazyGetIdeaOrdersQuery,
} from '@/apis/idea/idea-api.api.ts'
import { TotalReturnPctDependentHeaderComponent } from './total-return-pct-dependent-header.tsx'
import { isDisabledIdeaState } from '@/enums/management-input.enum.ts'
import '@/pages/idea-list/components/idea-list-table/idea-list-table.scss'
import { NEW_RELIC, newRelic } from '@/utils/new-relic.ts'
import { SSRMGrid } from '@/common/ag-grid/ssrm-grid.tsx'
import { useHighlightRow } from '@/common/ag-grid/hooks/use-grid-setting.ts'
import type {
  SSRMGridFetchParams,
  SSRMGridRef,
} from '@/common/ag-grid/grid.types.ts'
import {
  toIdeaListRequest,
  type IdeaListAdapterInput,
  type UnifiedIdeaRow,
} from './idea-list-ssrm-adapter.ts'
import { TickerCodeCellRenderer } from '@/common/ag-grid/ticker-code-cell-renderer.tsx'
import { useIdeaContextMenuItems } from './use-idea-context-menu-items.tsx'
import { FillingProgress } from '@/pages/idea-list/components/filling-progress.tsx'
import {
  FIRM_ROLE,
  ROLE_ADMIN,
  ROLE_CAPTAIN,
  ROLE_CONTRIBUTOR,
  ROLE_MANAGER,
  ROLE_MASTER,
  ROLE_VIEWONLY,
} from '@/router/roles'
import { setDashboardInputDateRange } from '@/redux/slices/dashboard-input.slice.ts'
import {
  HorizonOptions,
  ConvictionOptions,
  FillStrategies,
  getTradeReasonsTreeData,
} from '@/pages/idea-list/components/idea-modal/idea-action/action-idea-form.ts'

export interface IdeaListTableRef {
  refresh: () => void
  expandAll: () => void
  collapseAll: () => void
}

interface IdeaListTableProps {
  onRefreshed?: () => void
  onEditIdea: (id: string) => void
  onLiquidateIdea: (id: string) => void
  onAllExpandedChange?: (expanded: boolean) => void
  height: number | string
}

interface ActionCellRendererProps extends CustomCellRendererProps {
  onEditIdea: (id: string) => void
}

function ActionCellRenderer({ data, onEditIdea }: ActionCellRendererProps) {
  const userInfo = useAppSelector(selectUser)

  if (data?.rowType !== 'idea') return null
  if (!data?.canEdit) return null
  if (blockedFirmIdsCreateEditIdea.includes(userInfo.firmId)) return null

  return (
    <IdeyaButton icon={<EditOutlined />} onClick={() => onEditIdea(data.id)} />
  )
}

function DirectionCellRenderer(props: CustomCellRendererProps) {
  if (props.value === undefined) return <></>

  return <IdeaDirectionTag direction={props.data.direction} />
}

function FillingProgressCellRenderer(props: CustomCellRendererProps) {
  const value = props.value

  if (typeof value !== 'number' || isNaN(value)) return null

  // For order rows, follow the parent idea's state; otherwise use the row's own state
  const ideaState =
    props.data?.rowType === 'order'
      ? props.node.parent?.data?.state
      : props.data?.state
  return (
    <FillingProgress
      value={value}
      ideaState={ideaState}
      orderState={props.data?.state}
      isOrderRow={props.data?.rowType === 'order'}
      className={'idea-list-table__progress'}
    />
  )
}

function AutoFillCellRenderer(props: CustomCellRendererProps) {
  const { t } = useTranslation()
  return props.value === true
    ? t('common.true')
    : props.value === false
      ? t('common.false')
      : ''
}

function AlertCellRenderer(props: CustomCellRendererProps) {
  const themeStyle = useAppSelector(selectThemeStyle)
  const colorTheme = useAppSelector(selectTheme)

  if (props.data?.rowType !== 'idea') return null
  if (!Array.isArray(props.value)) return <></>

  const alertTypeMap = {
    [IdeaAlertType.Error]: {
      icon: (
        <CloseCircleFilled
          className={
            'idea-list-table__alert-icon idea-list-table__alert-icon--error'
          }
        />
      ),
    },
    [IdeaAlertType.Info]: {
      icon: (
        <InfoCircleFilled
          className={
            'idea-list-table__alert-icon idea-list-table__alert-icon--info'
          }
        />
      ),
    },
    [IdeaAlertType.Warning]: {
      icon: (
        <ExclamationCircleFilled
          className={
            'idea-list-table__alert-icon idea-list-table__alert-icon--warning'
          }
        />
      ),
    },
  }

  const alert = [...props.value].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )[0]

  if (!alert) return <></>

  const { icon } = alertTypeMap[alert.type as keyof typeof alertTypeMap] || {}

  return (
    <div className={'idea-list-table__alert'}>
      <Tooltip
        title={alert.message}
        color={colorTheme === Theme.Light ? 'white' : ''}
        styles={{
          body: {
            color: colorTheme === Theme.Light ? themeStyle.colorText : '',
          },
        }}
      >
        {icon}
      </Tooltip>
    </div>
  )
}

// Extract the latest alert type from row data
function getLatestAlertType(data: UnifiedIdeaRow | undefined): string | null {
  if (!data || !Array.isArray(data.alerts) || data.alerts.length === 0) {
    return null
  }

  const latestAlert = [...data.alerts]
    .filter((alert) => alert.type && alert.timestamp)
    .sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )[0]

  return latestAlert?.type ?? null
}

function isServerSideGroup(dataItem: unknown): boolean {
  return (dataItem as UnifiedIdeaRow).rowType === 'idea'
}

function getServerSideGroupKey(dataItem: unknown): string {
  return (dataItem as UnifiedIdeaRow).id
}

function getRowId(params: GetRowIdParams<UnifiedIdeaRow>): string {
  const { data, parentKeys = [] } = params
  const id = data.rowType === 'idea' ? data.id : (data.orderId ?? data.actionId)
  return [data.rowType, ...parentKeys, id].join(':')
}

const PAGE_SIZE = 30

const MOBILE_COLUMN_ORDER = [
  'actions',
  'tickerName',
  'pctBP',
  'dailyPnl',
  'totalReturnPct',
  'direction',
]

const textFilterParams = {
  filterOptions: ['contains'],
  debounceMs: 300,
  buttons: ['reset', 'apply'],
  maxNumConditions: 1,
  closeOnApply: true,
}

export function IdeaListTable({
  onRefreshed,
  onEditIdea,
  onLiquidateIdea,
  onAllExpandedChange,
  height,
  ref,
}: IdeaListTableProps & { ref?: React.Ref<IdeaListTableRef> }) {
  const ideaListInput = useSelector(selectIdeaListInput)
  const screens = useFixedBreakpoint()
  const isMobile = !screens.md
  const [activeFilterColumn, setActiveFilterColumn] = useState<string>('')
  const { t } = useTranslation()
  const userInfo = useAppSelector(selectUser)
  const ssrmRef = useRef<SSRMGridRef>(null)
  const dispatch = useAppDispatch()

  const [triggerListIdeas] = useLazyListIdeasQuery()
  const [triggerGetIdeaOrders] = useLazyGetIdeaOrdersQuery()

  // Shared row-highlight behavior (predicate + auto-clear of router state)
  const { isHighlightedRow } = useHighlightRow()

  // Whether the current user can access the dashboard page (gates ticker code link)
  const canAccessSideMenuDashboard = hasPermission(
    canAccessSideMenuDashboardPermissions,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  // region i18n value formatters (horizon / conviction / trade reason / fill strategy)

  // Flatten the trade reason tree (categories + children) into value → title map
  const tradeReasonLabelMap = useMemo(() => {
    const map = new Map<string, string>()
    getTradeReasonsTreeData(t).forEach((category) => {
      map.set(category.value, category.title)
      category.children?.forEach((child) => map.set(child.value, child.title))
    })
    return map
  }, [t])

  const horizonValueFormatter = useCallback(
    (params: ValueFormatterParams) => {
      const value = params.value as string | null | undefined
      if (!value) return ''
      const found = HorizonOptions.find((option) => option.value === value)
      return found ? t(found.labelKey) : value
    },
    [t]
  )

  const convictionValueFormatter = useCallback(
    (params: ValueFormatterParams) => {
      const value = params.value as string | null | undefined
      if (!value) return ''
      const found = ConvictionOptions.find((option) => option.value === value)
      return found ? t(found.labelKey) : value
    },
    [t]
  )

  const fillStrategyValueFormatter = useCallback(
    (params: ValueFormatterParams) => {
      const value = params.value as string | null | undefined
      if (!value) return ''
      const found = FillStrategies.find((option) => option.value === value)
      return found ? t(found.labelKey) : value
    },
    [t]
  )

  const tradeReasonValueFormatter = useCallback(
    (params: ValueFormatterParams) => {
      const value = params.value as string | null | undefined
      if (!value) return ''
      // tradeReason is a comma-separated string of multiple reasons;
      // split, translate each individually, then re-join.
      return (
        value
          ?.split(',')
          .map((reason) => reason.trim())
          .filter(Boolean)
          .map((reason) => tradeReasonLabelMap.get(reason) ?? reason)
          .join(', ') ?? ''
      )
    },
    [tradeReasonLabelMap]
  )

  // endregion

  // When viewing a historical end date, point-in-time metrics are shown as '-'
  const isHistorical = isHistoricalEndDate(ideaListInput.dateRange[1])

  const columnDefs = useMemo<ColDef[]>(() => {
    // Handle navigation state from dashboard position table (ticker code click)

    const baseColumns: ColDef[] = [
      // Hidden grouping column — drives SSRM Row Grouping by idea id
      {
        field: 'id',
        rowGroup: true,
        hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        headerName: '',
        suppressHeaderMenuButton: true,
        colId: 'actions',
        cellRenderer: ActionCellRenderer,
        cellRendererParams: {
          onEditIdea,
        },
        maxWidth: 70,
        minWidth: 70,
        sortable: false,
        pinned: isMobile ? null : 'left',
      },
      {
        headerName: t('idea_list.idea_table.columns.ticker_code'),
        field: 'tickerCode',
        filter: 'agTextColumnFilter',
        filterParams: textFilterParams,
        pinned: isMobile ? null : 'left',
        cellRenderer: TickerCodeCellRenderer,
        cellRendererParams: {
          tooltipTitle: t(
            'idea_list.idea_table.position_table_ticker_code_tooltip'
          ),
          navigateTo: '/dashboard',
          hasAccess: canAccessSideMenuDashboard,
          getNavigationState: (params: CustomCellRendererProps) => ({
            activeIdeaId: params.data.id,
          }),
          onBeforeNavigate: () => {
            const [start, end] = ideaListInput.dateRange
            dispatch(setDashboardInputDateRange([start ?? null, end ?? null]))
          },
          isNavigationDisabled: (params: CustomCellRendererProps) =>
            params.data?.rowType !== 'idea' || params.data.state !== 'Active',
        },
      },
      {
        headerName: t('idea_list.idea_table.columns.ticker_name'),
        field: 'tickerName',
        filter: 'agTextColumnFilter',
        filterParams: textFilterParams,
      },
      {
        headerName: t('idea_list.idea_table.columns.ticker_legal_name'),
        field: 'tickerLegalName',
      },
      {
        headerName: t('idea_list.idea_table.columns.direction'),
        field: 'direction',
        filter: 'agTextColumnFilter',
        filterParams: textFilterParams,
        cellRenderer: DirectionCellRenderer,
      },
      {
        headerName: t('idea_list.idea_table.columns.current_market_value'),
        field: 'currentMarketValue',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
      },
      {
        headerName: t('idea_list.idea_table.columns.filling_progress'),
        field: 'fillingProgress',
        type: 'numberColumn',
        cellRenderer: FillingProgressCellRenderer,
      },
      {
        headerName: t('idea_list.idea_table.columns.daily_return_pct'),
        field: 'dailyReturnPct',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          isHistorical ? '-' : percentageValueFormatter(params),
        cellStyle: isHistorical ? undefined : checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.quarterly_return_pct'),
        field: 'quarterlyReturnPct',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          isHistorical ? '-' : percentageValueFormatter(params),
        cellStyle: isHistorical ? undefined : checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.total_return_pct'),
        field: 'totalReturnPct',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          isHistorical ? '-' : percentageValueFormatter(params),
        cellStyle: isHistorical ? undefined : checkStateColoredNumberCellStyle,
        sortable: true,
        headerComponentParams: {
          innerHeaderComponent: TotalReturnPctDependentHeaderComponent,
        },
      },
      {
        headerName: t('idea_list.idea_table.columns.daily_pnl'),
        field: 'dailyPnl',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
        cellStyle: checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.quarter_pnl'),
        field: 'quarterPnl',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          isHistorical
            ? '-'
            : currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
        cellStyle: isHistorical ? undefined : checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.current_total_pnl'),
        field: 'currentTotalPnl',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
        cellStyle: checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.cumulative_pnl'),
        field: 'cumulativePnl',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
        cellStyle: checkStateColoredNumberCellStyle,
      },
      {
        headerName: t('idea_list.idea_table.columns.target_market_value'),
        field: 'targetMarketValue',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          isHistorical
            ? '-'
            : currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
      },
      {
        headerName: t('idea_list.idea_table.columns.avg_cost'),
        field: 'avgCost',
        type: 'numberColumn',
        valueFormatter: currencyValueFormatter,
      },
      {
        headerName: t('idea_list.idea_table.columns.current_qty'),
        field: 'currentQty',
        type: 'numberColumn',
      },
      {
        headerName: t('idea_list.idea_table.columns.target_qty'),
        field: 'targetQty',
        type: 'numberColumn',
        valueFormatter: (params: ValueFormatterParams) =>
          isHistorical
            ? '-'
            : params.value
              ? params.value.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })
              : '0',
      },
      {
        headerName: t('idea_list.idea_table.columns.currency'),
        field: 'tickerCurrency',
      },
      {
        headerName: t('idea_list.idea_table.columns.targetPrice'),
        field: 'targetPrice',
        type: 'numberColumn',
        valueFormatter: currencyValueFormatter,
      },
      {
        headerName: t('idea_list.idea_table.columns.stopPrice'),
        field: 'stopPrice',
        type: 'numberColumn',
        valueFormatter: currencyValueFormatter,
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.horizon'
        ),
        field: 'horizon',
        valueFormatter: horizonValueFormatter,
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.conviction'
        ),
        field: 'conviction',
        valueFormatter: convictionValueFormatter,
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.trade_reason'
        ),
        field: 'tradeReason',
        valueFormatter: tradeReasonValueFormatter,
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.fill_strategy'
        ),
        field: 'fillStrategy',
        valueFormatter: fillStrategyValueFormatter,
      },
      {
        headerName: t('idea_list.idea_table.columns.auto_fill'),
        field: 'auto_fill',
        cellRenderer: AutoFillCellRenderer,
        sortable: false,
      },
      {
        headerName: t('idea_list.idea_table.columns.created_at'),
        field: 'createdAt',
        type: ['dateColumn'],
      },
      {
        headerName: t('idea_list.idea_table.columns.state'),
        field: 'state',
        filter: 'agTextColumnFilter',
        filterParams: textFilterParams,
        valueFormatter: (params: ValueFormatterParams) =>
          params.data?.rowType === 'order' ? '' : params.value,
      },
      {
        headerName: t('idea_list.idea_table.columns.overall_cost'),
        field: 'overallCost',
        type: 'numberColumn',
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
      },
      {
        headerName: t('idea_list.idea_table.columns.alert'),
        field: 'alerts',
        suppressHeaderMenuButton: true,
        sortable: false,
        hide: true,
        cellRenderer: AlertCellRenderer,
        maxWidth: 90,
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.liquidate_reason'
        ),
        field: 'liquidateReason',
        hide: true,
      },
      {
        headerName: t('idea_list.idea_table.columns.closed_at'),
        field: 'closedAt',
        type: ['dateColumn'],
        hide: true,
      },
      {
        headerName: t('idea_list.idea_table.columns.duration'),
        field: 'duration',
        type: 'numberColumn',
        hide: true,
        valueFormatter: (params: ValueFormatterParams) => {
          // Order rows have no duration of their own; keep them blank.
          if (params.data?.rowType === 'order') return ''
          return isHistorical ? '-' : (params.value ?? '')
        },
      },
      {
        headerName: t('idea_list.idea_table.columns.contributor'),
        field: 'contributor',
        filter: 'agTextColumnFilter',
        hide: userInfo.groupRole === ROLE_CONTRIBUTOR,
        filterParams: textFilterParams,
      },
      {
        headerName: t('idea_list.idea_table.columns.firm_name'),
        field: 'firmName',
        filter: 'agTextColumnFilter',
        hide:
          userInfo.groupRole === ROLE_CONTRIBUTOR ||
          userInfo.groupRole === ROLE_CAPTAIN ||
          userInfo.groupRole === ROLE_MANAGER ||
          userInfo.firmRole === FIRM_ROLE.ROLE_COMPLIANCE,
        filterParams: textFilterParams,
      },
    ]

    // Only show account type for internal admin users
    if (
      userInfo.role === ROLE_ADMIN ||
      userInfo.role === ROLE_MASTER ||
      userInfo.role === ROLE_VIEWONLY
    ) {
      baseColumns.push({
        headerName: t('idea_list.idea_table.columns.account_type'),
        field: 'isTrial',
        cellRenderer: IsTrialCellRenderer,
        sortable: false,
      })
    }

    return baseColumns
  }, [
    userInfo,
    onEditIdea,
    isMobile,
    t,
    dispatch,
    ideaListInput.dateRange,
    canAccessSideMenuDashboard,
    horizonValueFormatter,
    convictionValueFormatter,
    tradeReasonValueFormatter,
    fillStrategyValueFormatter,
    isHistorical,
  ])

  // region fetchData (SSRM adapter-based)

  const adapterInput = useMemo<IdeaListAdapterInput>(
    () => ({
      showLiquidatedIdeas: ideaListInput.showLiquidatedIdeas,
      firm: ideaListInput.firm,
      contributor: ideaListInput.contributor,
      category: ideaListInput.category,
      dateRange: ideaListInput.dateRange,
      onlyMyIdea: ideaListInput.onlyMyIdea,
      userSub: userInfo.sub,
      userRole: userInfo.role,
      userGroupRole: userInfo.groupRole,
      userFirmRole: userInfo.firmRole,
    }),
    [
      ideaListInput.showLiquidatedIdeas,
      ideaListInput.firm,
      ideaListInput.contributor,
      ideaListInput.category,
      ideaListInput.dateRange,
      ideaListInput.onlyMyIdea,
      userInfo.sub,
      userInfo.role,
      userInfo.groupRole,
      userInfo.firmRole,
    ]
  )

  const fetchData = useCallback(
    async (
      params: SSRMGridFetchParams
    ): Promise<{ rows: unknown[]; totalCount?: number }> => {
      // Child row request: fetch order rows for a specific idea.
      // Orders have no timezone of their own, so inherit marketTimeZone from
      // the parent idea row (only available here via the grid's parent node).
      if (params.groupKeys.length > 0) {
        const ideaId = params.groupKeys[0]
        const parentMarketTimeZone =
          (params.parentData as UnifiedIdeaRow | undefined)?.marketTimeZone ??
          null
        const orderRows = await triggerGetIdeaOrders(ideaId).unwrap()
        const rowsWithTimeZone = orderRows.map((row) => ({
          ...row,
          marketTimeZone: parentMarketTimeZone,
        }))
        return { rows: rowsWithTimeZone, totalCount: rowsWithTimeZone.length }
      }

      // Top-level request: fetch idea list
      const request = toIdeaListRequest(params, adapterInput, PAGE_SIZE)
      const result = await triggerListIdeas(request).unwrap()

      // TEMP WORKAROUND: backend `total` currently returns the current page size
      // instead of the overall count. Leave rowCount unknown while pages are full;
      // otherwise concurrent blocks can report conflicting counts after a sort and
      // a late response from an earlier page can truncate the server-side store.
      const rowsLen = result.rows.length
      const totalCount =
        rowsLen < PAGE_SIZE ? params.startRow + rowsLen : undefined

      return { rows: result.rows, totalCount }
    },
    [adapterInput, triggerListIdeas, triggerGetIdeaOrders]
  )

  // endregion

  // region Filter Changed (single-column restriction)

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      if (!event.api) return

      const filterModel = event.api.getFilterModel()
      const filterColumns = Object.keys(filterModel)

      if (filterColumns.length === 0) {
        setActiveFilterColumn('')
        return
      }

      if (filterColumns.length > 1) {
        const newFilter = filterColumns.find(
          (col) => col !== activeFilterColumn
        )
        if (newFilter) {
          const newFilterModel = { [newFilter]: filterModel[newFilter] }
          event.api.setFilterModel(newFilterModel)
          setActiveFilterColumn(newFilter)
          return
        }
      }

      if (filterColumns.length === 1) {
        setActiveFilterColumn(filterColumns[0])
      }

      // Enforce single-column restriction
      const changedColumn = filterColumns[filterColumns.length - 1]
      if (changedColumn && filterModel[changedColumn]) {
        if (changedColumn !== activeFilterColumn && activeFilterColumn) {
          const newFilterModel = {
            [changedColumn]: filterModel[changedColumn],
          }
          event.api.setFilterModel(newFilterModel)
        }
        setActiveFilterColumn(changedColumn)
      }
    },
    [activeFilterColumn]
  )

  // endregion

  // region Context Menu

  const getContextMenuItems = useIdeaContextMenuItems({
    onEditIdea,
    onLiquidateIdea,
    onRefresh: () => ssrmRef.current?.refresh(),
  })

  // endregion

  // region Row Class Rules

  const rowClassRules = useMemo<RowClassRules>(
    () => ({
      // Only apply row styles to parent idea rows, not child action log rows
      'ag-row--filling-ignore': (params) =>
        params.data?.rowType === 'idea' &&
        params.data?.lastFillingState === 'Ignore',

      'ag-row--filling-filled': (params) =>
        params.data?.rowType === 'idea' &&
        params.data?.lastFillingState === 'Filled',

      'ag-row--state-disabled': (params) => {
        // For order rows, apply when the parent idea's state is disabled
        // or when the order itself is Canceled
        if (params.data?.rowType === 'order') {
          return (
            isDisabledIdeaState(params.node.parent?.data?.state) ||
            params.data?.state === 'Canceled'
          )
        }
        return isDisabledIdeaState(params.data?.state)
      },

      'ag-row--order-level': (params) => params.data?.rowType === 'order',

      'ag-row--alert-info': (params) =>
        params.data?.rowType === 'idea' &&
        getLatestAlertType(params.data) === IdeaAlertType.Info,

      'ag-row--alert-warning': (params) =>
        params.data?.rowType === 'idea' &&
        getLatestAlertType(params.data) === IdeaAlertType.Warning,

      'ag-row--alert-error': (params) =>
        params.data?.rowType === 'idea' &&
        getLatestAlertType(params.data) === IdeaAlertType.Error,

      'ag-row--hightlighted': isHighlightedRow,
    }),
    [isHighlightedRow]
  )

  // endregion

  // region Column Event (New Relic tracking)

  const handleColumnEvent = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (eventType: string, _event: ColumnEvent | SortChangedEvent) => {
      newRelic.recordCustomEvent('IdeaListTableCustomizeAction', {
        element:
          eventType === 'sortChanged'
            ? NEW_RELIC.LIST_CUSTOMIZE_ACTION.sortChange
            : eventType === 'columnResized'
              ? NEW_RELIC.LIST_CUSTOMIZE_ACTION.columnResize
              : NEW_RELIC.LIST_CUSTOMIZE_ACTION.columnChange,
        event: `${eventType} - ${userInfo.email}`,
      })
    },
    [userInfo.email]
  )

  // endregion

  // region Imperative Ref

  useImperativeHandle(
    ref,
    (): IdeaListTableRef => ({
      refresh: () => ssrmRef.current?.refresh(),
      expandAll: () => ssrmRef.current?.setAllGroupsExpanded(true),
      collapseAll: () => {
        ssrmRef.current?.setAllGroupsExpanded(false)
        const api = ssrmRef.current?.getGridApi()
        if (!api) return
        // Scroll to top before shrinking; otherwise the browser clamps scrollTop
        // and AG Grid may keep a stale render range, showing only the last row.
        if (api.getDisplayedRowCount() > 0) api.ensureIndexVisible(0, 'top')
      },
    }),
    []
  )

  // endregion

  // region External Query Refresh

  // The grid datasource is intentionally stable, so external query inputs
  // (date range, firm, contributor, category, etc.) no longer reset the grid
  // implicitly. Reload silently when they change so stale rows from the
  // previous query are replaced. Uses reload() (not refresh()) so a changed
  // query always re-fetches instead of being coalesced into an in-flight
  // refresh. Depends on the serialized value — not the object identity — so
  // re-selecting the same query still reloads deterministically.
  const adapterInputKey = JSON.stringify(adapterInput)
  const isFirstAdapterRunRef = useRef(true)
  useEffect(() => {
    if (isFirstAdapterRunRef.current) {
      isFirstAdapterRunRef.current = false
      return
    }
    ssrmRef.current?.reload()
  }, [adapterInputKey])

  // endregion

  // region Auto Group Column (expand/collapse column for row grouping)

  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      headerName: '',
      minWidth: 40,
      maxWidth: 40,
      suppressHeaderMenuButton: true,
      sortable: false,
      filter: false,
      resizable: false,
      pinned: isMobile ? null : 'left',
      cellRendererParams: {
        // Hide group value text, only show expand/collapse arrow
        innerRenderer: () => null,
        suppressCount: true,
      },
    }),
    [isMobile]
  )

  // endregion

  return (
    <SSRMGrid
      ref={ssrmRef}
      columnDefs={columnDefs}
      storageKey={IDEA_LIST_COLUMN_STATE_KEY}
      sortMapping={sortMappingIdeaList}
      fetchData={fetchData}
      getContextMenuItems={getContextMenuItems}
      rowClassRules={rowClassRules}
      onFilterChanged={handleFilterChanged}
      onColumnEvent={handleColumnEvent}
      autoGroupColumnDef={autoGroupColumnDef}
      getRowId={getRowId}
      isServerSideGroup={isServerSideGroup}
      getServerSideGroupKey={getServerSideGroupKey}
      maxBlocksInCache={10}
      onAllGroupsExpandedChange={onAllExpandedChange}
      height={height}
      className={'idea-list-table'}
      onRefreshed={onRefreshed}
      autoRefreshInterval={Number(import.meta.env.VITE_GET_IDEA_LIST_INTERVAL)}
      mobileColumnOrder={MOBILE_COLUMN_ORDER}
      tutorialColId={'tickerCode'}
      pageSize={PAGE_SIZE}
    />
  )
}
